import { useEffect, useState } from "react";
import { AlertTriangle, Sun, ArrowRight, Loader2, Users as UsersIcon, TrendingUp, Mail, Radio } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import SignalGauge from "@/components/SignalGauge";
import HUDFrame from "@/components/HUDFrame";

const dayLabel = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

// Sparkline: given points array of {date, value} render a compact 24×80 svg
function Sparkline({ points, tone = "teal", testId }) {
  if (!points || points.length === 0) {
    return (
      <div className="flex h-14 items-center font-mono text-[10px] uppercase tracking-[0.22em] text-mist/60" data-testid={testId}>
        No history yet
      </div>
    );
  }
  const w = 200;
  const h = 56;
  const values = points.map((p) => p.value);
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 100);
  const range = Math.max(1, max - min);
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const path = points
    .map((p, i) => {
      const x = i * step;
      const y = h - ((p.value - min) / range) * h;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const stroke =
    tone === "amber" ? "#D98A44" : tone === "red" ? "#B75555" : tone === "green" ? "#4E8B7C" : "#2E7788";
  const last = points[points.length - 1];
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" data-testid={testId} className="w-full">
      <defs>
        <linearGradient id={`spg-${tone}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.35" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${path} L${w},${h} L0,${h} Z`} fill={`url(#spg-${tone})`} />
      <path d={path} fill="none" stroke={stroke} strokeWidth="1.5" />
      {last && (
        <circle
          cx={((points.length - 1) * step).toFixed(1)}
          cy={(h - ((last.value - min) / range) * h).toFixed(1)}
          r="2.5"
          fill={stroke}
        />
      )}
    </svg>
  );
}

const dayLabel_ = dayLabel; // no-op alias to preserve above

export default function Tower() {
  const [signals, setSignals] = useState(null);
  const [err, setErr] = useState(null);
  const [history, setHistory] = useState(null);
  const [assessBusy, setAssessBusy] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [drillOpen, setDrillOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/signals")
      .then((r) => !cancelled && setSignals(r.data))
      .catch((e) => !cancelled && setErr(e?.response?.data?.detail || "Signals unavailable."));
    api
      .get("/signals/history", { params: { days: 14 } })
      .then((r) => !cancelled && setHistory(r.data))
      .catch(() => !cancelled && setHistory({ points: [] }));
    return () => {
      cancelled = true;
    };
  }, []);

  const runAssess = async () => {
    setAssessBusy(true);
    try {
      const { data } = await api.post("/household/radar/assess");
      toast.success(`Radar re-assessed ${data.assessed} event${data.assessed === 1 ? "" : "s"}. ${data.new_reds} new red landing${data.new_reds === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Radar assessment failed.");
    } finally {
      setAssessBusy(false);
    }
  };

  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post("/household/digest/send-now");
      toast.success(`Digest dispatched to ${data.sent.length} household member${data.sent.length === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send digest.");
    } finally {
      setDigestBusy(false);
    }
  };

  if (err) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 text-mist" data-testid="tower-error">
        {String(err)}
      </div>
    );
  }
  if (!signals) {
    return (
      <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-16 font-mono text-[11px] uppercase tracking-[0.22em] text-mist" data-testid="tower-loading">
        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
        Reading the tower…
      </div>
    );
  }

  const outlookTint =
    signals.capacity_status === "red" || signals.recovery_status === "red"
      ? "text-signalRed"
      : signals.capacity_status === "amber" || signals.recovery_status === "amber"
        ? "text-signalAmber"
        : "text-signalGreen";

  const sparkPoints = (key) =>
    (history?.points || []).map((p) => ({ date: p.date, value: p[key] }));

  return (
    <div data-testid="tower-page" className="mx-auto max-w-7xl px-6 py-10 md:px-8 md:py-12">
      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">
            T-01 · Tower
          </p>
          <h1 className="mt-4 font-display text-4xl leading-[1.05] text-ink md:text-5xl" data-testid="tower-outlook">
            The household is
            <br />
            <span className={`italic ${outlookTint}`}>{signals.outlook}</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-mist">
            Live reading from your household profile —{" "}
            <span className="text-ink">{signals.committed_hours}h</span> committed against{" "}
            <span className="text-ink">{signals.available_hours}h</span> available, with{" "}
            <span className="text-ink">{signals.protected_hours}h</span> defended.
          </p>

          {/* Load attribution — who's driving compression this week */}
          {(signals.attribution_note || (signals.load_contributors && signals.load_contributors.length > 0)) && (
            <div className="mt-6 border border-rule bg-deep/40 px-4 py-3" data-testid="tower-attribution">
              <div className="flex items-start gap-3">
                <UsersIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal" strokeWidth={1.5} />
                <div className="flex-1">
                  {signals.attribution_note ? (
                    <p className="text-sm leading-relaxed text-ink">{signals.attribution_note}</p>
                  ) : (
                    <p className="text-sm leading-relaxed text-mist">
                      Load is spread evenly across household members this week.
                    </p>
                  )}
                  {signals.load_contributors && signals.load_contributors.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="tower-contributors">
                      {signals.load_contributors.map((c) => (
                        <span
                          key={c.user_id}
                          data-testid={`contributor-${c.user_id}`}
                          className={`inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.22em] ${
                            c.is_top ? "border-teal/60 text-ink" : "border-rule text-mist"
                          }`}
                        >
                          {c.is_top && <span className="h-1.5 w-1.5 rounded-full bg-teal" />}
                          {c.name} · {c.contribution_pct}%
                        </span>
                      ))}
                    </div>
                  )}
                  {signals.daily_attribution && signals.daily_attribution.length > 0 && (
                    <button
                      onClick={() => setDrillOpen((v) => !v)}
                      data-testid="tower-drill-toggle"
                      className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-teal hover:text-ink"
                    >
                      {drillOpen ? "Hide" : "Show"} full week breakdown
                    </button>
                  )}
                </div>
              </div>
              {drillOpen && signals.daily_attribution && (
                <div className="mt-4 border-t border-rule pt-4" data-testid="tower-drilldown">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {signals.daily_attribution.map((d) => (
                      <div key={d.day} className="border border-rule bg-obsidian/60 p-3">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                            {dayLabel[d.day]}
                          </span>
                          <span
                            className={`font-mono text-xs ${
                              d.status === "green"
                                ? "text-signalGreen"
                                : d.status === "red"
                                  ? "text-signalRed"
                                  : "text-signalAmber"
                            }`}
                          >
                            {d.load}%
                          </span>
                        </div>
                        {d.contributors.length === 0 ? (
                          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mist/60">
                            Quiet
                          </p>
                        ) : (
                          <ul className="mt-2 space-y-1">
                            {d.contributors.slice(0, 4).map((c) => (
                              <li
                                key={c.user_id}
                                className="flex items-center justify-between text-xs"
                                data-testid={`drill-${d.day}-${c.user_id}`}
                              >
                                <span className="truncate text-ink">{c.name}</span>
                                <span className="ml-2 font-mono text-mist">{c.hours}h · {c.pct}%</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Manual actions */}
          <div className="mt-6 flex flex-wrap gap-2">
            <button
              onClick={runAssess}
              disabled={assessBusy}
              data-testid="tower-radar-assess-btn"
              className="inline-flex items-center gap-2 border border-rule bg-deep/40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mist transition-colors duration-300 hover:border-teal hover:text-ink disabled:opacity-50"
            >
              <Radio className="h-3 w-3" strokeWidth={1.5} />
              {assessBusy ? "Sweeping…" : "Sweep radar now"}
            </button>
            <button
              onClick={sendDigest}
              disabled={digestBusy}
              data-testid="tower-digest-btn"
              className="inline-flex items-center gap-2 border border-rule bg-deep/40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mist transition-colors duration-300 hover:border-teal hover:text-ink disabled:opacity-50"
            >
              <Mail className="h-3 w-3" strokeWidth={1.5} />
              {digestBusy ? "Sending…" : "Send digest now"}
            </button>
          </div>
        </div>

        <HUDFrame className="bg-deep/60 p-6" tone="teal">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            <Sun className="h-3.5 w-3.5 text-amber" strokeWidth={1.5} />
            Focus of the day
          </div>
          <p className="mt-4 font-display text-xl leading-snug text-ink" data-testid="tower-focus">
            {signals.focus_of_day}
          </p>
          <div className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-teal">
            Suggested by tower <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
          </div>
        </HUDFrame>
      </div>

      <div className="mt-10 grid gap-6 md:grid-cols-3">
        <SignalGauge
          label="Capacity Load"
          value={signals.capacity_load}
          status={signals.capacity_status}
          hint="How much of the household's sustainable capacity is committed right now."
          testId="tower-gauge-capacity"
        />
        <SignalGauge
          label="Compression Index"
          value={signals.compression_index}
          status={signals.compression_status}
          hint="How tightly commitments are packed against buffer, transitions, and rest."
          testId="tower-gauge-compression"
        />
        <SignalGauge
          label="Recovery Margin"
          value={signals.recovery_margin}
          status={signals.recovery_status}
          hint="How much protected recovery time remains this week."
          testId="tower-gauge-recovery"
        />
      </div>

      {/* Trend strip — 14-day sparklines */}
      <HUDFrame className="mt-10 bg-deep/40 p-6 md:p-8" tone="teal">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-teal">
          <TrendingUp className="h-3.5 w-3.5" strokeWidth={1.5} />
          Trend · last {history?.days || 14} days
        </div>
        <div className="mt-6 grid gap-6 md:grid-cols-3" data-testid="tower-trend-strip">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">Capacity</div>
            <Sparkline points={sparkPoints("capacity_load")} tone="teal" testId="trend-capacity" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">Compression</div>
            <Sparkline points={sparkPoints("compression_index")} tone="amber" testId="trend-compression" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">Recovery</div>
            <Sparkline points={sparkPoints("recovery_margin")} tone="green" testId="trend-recovery" />
          </div>
        </div>
      </HUDFrame>

      <div className="mt-10 grid gap-6 md:grid-cols-2">
        <HUDFrame className="bg-deep/40 p-8" tone="amber">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-signalAmber">
            <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} />
            Watchouts
          </div>
          <ul className="mt-5 space-y-3 text-sm text-ink" data-testid="tower-watchouts">
            {signals.watchouts.map((w, i) => (
              <li
                key={i}
                className="border-b border-rule pb-3 leading-relaxed last:border-b-0 last:pb-0"
              >
                {w}
              </li>
            ))}
          </ul>
        </HUDFrame>

        <HUDFrame className="bg-deep/40 p-8" tone="teal">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-teal">
            Weekly load — per day
          </div>
          <div className="mt-6 grid grid-cols-7 gap-2" data-testid="tower-day-loads">
            {signals.day_loads.map((d) => {
              const color =
                d.status === "green"
                  ? "bg-signalGreen"
                  : d.status === "red"
                    ? "bg-signalRed"
                    : "bg-signalAmber";
              return (
                <div key={d.day} className="flex flex-col items-center gap-2">
                  <div className="flex h-24 w-full items-end bg-rule/40">
                    <div className={`w-full ${color}`} style={{ height: `${Math.max(4, d.load)}%` }} />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist">
                    {dayLabel_[d.day]}
                  </span>
                  <span className="font-mono text-[10px] text-mist/70">{d.load}%</span>
                </div>
              );
            })}
          </div>
        </HUDFrame>
      </div>
    </div>
  );
}
